import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../../services/api";
import "./archived-teachers.css";

export default function ArchivedTeachers() {
  const school = JSON.parse(localStorage.getItem("schoolAdmin"));
  const navigate = useNavigate();

  const [teachers, setTeachers] = useState([]);
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState("");

  // ================= LOAD ARCHIVED =================
  const loadArchived = async () => {
    try {
      setLoading(true);
      setError("");
      const res = await API.get(
        "/school-admin/teachers/archived/",
        { headers: { "X-SCHOOL-ID": school.school.id } }
      );
      setTeachers(res.data.teachers || []);
    } catch (err) {
      console.error(err);
      setError("Unable to load archived teachers.");
      setTeachers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadArchived();
  }, []);
  const deleteTeacher = async (id) => {
    const ok = window.confirm(
      "⚠️ This will permanently delete the teacher.\nThis action cannot be undone.\n\nContinue?"
    );
    if (!ok) return;

    await API.delete(
      `/school-admin/teachers/${id}/delete-permanent/`,
      { headers: { "X-SCHOOL-ID": school.school.id } }
    );

    loadArchived();
    setSelectedTeacher(null);
  };

  // ================= LOAD DETAILS =================
  const loadTeacherDetails = async (id) => {
    setLoadingDetails(true);
    try {
      const res = await API.get(`/school-admin/teachers/${id}/`, {
        headers: { "X-SCHOOL-ID": school.school.id },
      });
      setSelectedTeacher(res.data);
    } finally {
      setLoadingDetails(false);
    }
  };

  // ================= RESTORE =================
  const restoreTeacher = async (id) => {
    if (!window.confirm("Restore this teacher?")) return;

    await API.post(
      `/school-admin/teachers/${id}/restore/`,
      {},
      { headers: { "X-SCHOOL-ID": school.school.id } }
    );

    setSelectedTeacher(null);
    loadArchived();
  };

  return (

      <div className="archive-teachers-page">

        <div className="archive-hero">
          <div className="archive-hero-left">
            <span className="archive-hero-icon">📦</span>
            <div>
              <h1>Archived Teachers</h1>
              <p>{teachers.length} archived faculty records</p>
            </div>
          </div>

          <button
            className="archive-back-btn"
            type="button"
            onClick={() => navigate("/school-admin/teachers")}
          >
            Teacher List
          </button>
        </div>

      <div className="teacher-container split">

        {/* ================= LEFT: LIST ================= */}
        <div className="left-panel">
          <div className="archive-panel-head">
            <div>
              <h2>Archive</h2>
              <p>Select a record to inspect or restore</p>
            </div>
          </div>

          {loading ? (
            <div className="archive-state">
              <span className="archive-loader"></span>
              <strong>Loading archive</strong>
              <p>Fetching archived teacher records.</p>
            </div>
          ) : error ? (
            <div className="archive-state archive-error">
              <strong>{error}</strong>
              <p>Try loading the archive again.</p>
              <button onClick={loadArchived}>Retry</button>
            </div>
          ) : teachers.length === 0 ? (
            <div className="archive-state">
              <strong>No archived teachers</strong>
              <p>Archived faculty records will appear here.</p>
            </div>
          ) : (
            <div className="archive-list">
              {teachers.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`archive-teacher-card ${selectedTeacher?.id === t.id ? "active" : ""
                      }`}
                    onClick={() => loadTeacherDetails(t.id)}
                  >
                    <span className="archive-avatar">
                      {t.name?.charAt(0) || "T"}
                    </span>

                    <span className="archive-teacher-main">
                      <strong>{t.name || "Teacher"}</strong>
                      <small>{t.email || "No email"}</small>
                    </span>

                    <span className="archive-phone">
                      {t.phone || "-"}
                    </span>
                  </button>
                ))
              }
            </div>
          )}
        </div>

        {/* ================= RIGHT: DETAILS ================= */}
        <div className="right-panel">
          {!selectedTeacher ? (
            <div className="empty-details">
              <span>📋</span>
              <strong>Select a teacher</strong>
              <p>Details, attendance, salary, and assignments will appear here.</p>
            </div>
          ) : loadingDetails ? (
            <div className="archive-state">
              <span className="archive-loader"></span>
              <strong>Loading details</strong>
            </div>
          ) : (
            <>
              <div className="archive-detail-hero">
                <div className="archive-detail-avatar">
                  {selectedTeacher.name?.charAt(0) || "T"}
                </div>
                <div>
                  <h3>{selectedTeacher.name}</h3>
                  <p>{selectedTeacher.teacher_id || "No teacher ID"}</p>
                </div>
              </div>

              <div className="archive-info-grid">
                <div><span>Email</span><strong>{selectedTeacher.email || "-"}</strong></div>
                <div><span>Phone</span><strong>{selectedTeacher.phone || "-"}</strong></div>
                <div><span>Joining Date</span><strong>{selectedTeacher.joining_date || "-"}</strong></div>
                <div><span>Address</span><strong>{selectedTeacher.address || "-"}</strong></div>
              </div>

              {/* ===== ATTENDANCE ===== */}
              <div className="stat-card">
                <h4>Attendance</h4>
                <div className="archive-mini-grid">
                  <p>Present <b>{selectedTeacher.attendance_summary?.present || 0}</b></p>
                  <p>Absent <b>{selectedTeacher.attendance_summary?.absent || 0}</b></p>
                  <p>Leave <b>{selectedTeacher.attendance_summary?.leave || 0}</b></p>
                  <p>Total <b>{selectedTeacher.attendance_summary?.total_days || 0}</b></p>
                </div>
              </div>

              {/* ===== SALARY ===== */}
              <div className="stat-card">
                <h4>Salary</h4>
                <div className="archive-mini-grid">
                  <p>Monthly <b>₹{selectedTeacher.salary_summary?.monthly_salary || 0}</b></p>
                  <p>Paid <b className="green">₹{selectedTeacher.salary_summary?.paid_amount || 0}</b></p>
                  <p>Pending <b className="red">₹{selectedTeacher.salary_summary?.pending_amount || 0}</b></p>
                </div>
              </div>

              {/* ===== ASSIGNMENTS ===== */}
              <h4 className="mt-10">Assigned Classes</h4>

              {(selectedTeacher.assignments || []).length === 0 ? (
                <div className="archive-inline-empty">No assignments recorded</div>
              ) : (
                <table className="teacher-table small">
                  <thead>
                    <tr>
                      <th>Class</th>
                      <th>Section</th>
                      <th>Subject</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedTeacher.assignments.map((a, i) => (
                      <tr key={i}>
                        <td>{a.class}</td>
                        <td>{a.section}</td>
                        <td>{a.subject}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* ===== ACTION ===== */}
              <div className="archive-actions">
              <button
                className="btn-primary"
                onClick={() => restoreTeacher(selectedTeacher.id)}
              >
                Restore Teacher
              </button>

                <button
                  className="btn-primary danger"
                  onClick={() => deleteTeacher(selectedTeacher.id)}
                >
                  Delete Permanently
                </button>
              </div>
            </>
          )}
        </div>

      </div>
      </div>

  );
}
